import {defaultHandlerCatch, defaultHandlerResolver, promisifyImage} from "./helpers";
import {photolabTask} from "../api";
import Processing from "../Processing";
import {ageGroups} from "../etc";
import {mapAgeGroupToPromptString} from "./d22.handler";
import {BadResultError} from "../errors";
import {PhotolabTaskBuilder} from "@vicman-wi/photolab-api-js";

const tasksCache = {};

function photolabTaskHelper(creative, taskName, taskConfig, options) {
  const taskCacheKey = JSON.stringify(taskConfig);
  const _options = Object.assign({
    timeout: 1000,
    interval: 1000,
  }, options);

  if (!tasksCache[taskCacheKey]) {
    tasksCache[taskCacheKey] = photolabTask(taskConfig, {
      timeout: _options.timeout,
      interval: _options.interval,
    })
  }

  return tasksCache[taskCacheKey].then((taskResult) => {
    creative.setTask(taskName, taskResult);
    return taskResult;
  }).catch((err) => {
    delete tasksCache[taskCacheKey];
    throw err;
  });
}

async function checkImageIsAdultness(processing, imageUrl) {
  const questionsResults = await Promise.all([
    photolabTask(new PhotolabTaskBuilder()
      .setLanguage(processing.language)
      .addCollageMethod({"@configId": "looklab_nsfw_questions"})
      .addImage(imageUrl)
      .build(), {timeout: 500,interval: 500})
      .then((result) => {
        return {
          is_nude: result.answers[0],
          is_naked: result.answers[1],
          naked_boobs: result.answers[2],
          has_nudity: result.answers[3],
          has_porno: result.answers[4],
          is_porno: result.answers[5],
          is_pornography: result.answers[6],
          porno: result.answers[7],
          naked_woman_boobs_visible: result.answers[8],
          has_explicit_nudity: result.answers[9],
        };
      }),
    photolabTask(new PhotolabTaskBuilder()
      .setLanguage(processing.language)
      .addCollageMethod({"@configId": "looklab_nsfw_questions_2"})
      .addImage(imageUrl)
      .build(), {timeout: 500,interval: 500})
      .then((result) => {
        return {
          has_naked_bottom: result.answers[0],
          has_sensual_lingerie: result.answers[1],
          has_erotic: result.answers[2],
          is_erotic: result.answers[3],
          seductive_woman_in_lingerie: result.answers[4],
          has_gun: result.answers[5],
          has_weapon: result.answers[6],
          has_gun_pointed_at_the_viewer: result.answers[7],
          is_this_a_child: result.answers[8],
          is_it_a_naked_child: result.answers[9],
          child_nudity: result.answers[10],
          is_it_a_child_in_a_swimsuit: result.answers[11],
          child_in_underwear: result.answers[12],
          child_erotic: result.answers[13],
        };
      }),
  ]);

  const answers = Object.assign({}, ...questionsResults);

  return {
    answers,
    isAdult: Object.values(answers).filter((a) => a.toLowerCase() !== "no").length > 3,
  };
}

async function roopTask(processing, creative, userFileUrl, promptResultFileUrl) {
  let targetFaceIndex = 0;
  const getFacesTaskResult = await photolabTaskHelper(creative, "roop_get_faces", new PhotolabTaskBuilder()
    .setLanguage(processing.language)
    .addImage(promptResultFileUrl)
    .addCollageMethod({template_name: "nn_roop", mode: "get_faces"})
    .build());

  if (getFacesTaskResult.humans?.items?.length > 1) {
    targetFaceIndex = getFacesTaskResult.humans.items
      .reduce((v, {bbox}, index) => {
        const square = (bbox.maxPoint.x - bbox.minPoint.x) * (bbox.maxPoint.y - bbox.minPoint.y);
        if (v.square < square) {
          return {index, square};
        } else {
          return v;
        }
      }, {index: 0, square: 0})
      .index;
  }

  return await photolabTaskHelper(creative, "roop", new PhotolabTaskBuilder()
    .setLanguage(processing.language)
    .addImage(userFileUrl)
    .addImage(promptResultFileUrl)
    .addCollageMethod({template_name: "nn_roop", target_face_index: targetFaceIndex})
    .build());
}

/**
 * @param {Processing} processing
 * @param {Creative} creative
 */

export default (processing, creative) => {
  return new Promise(async (resolve, reject) => {
    try {
      const {answers} = processing.getExtra(Processing.EXTRA_QUESTIONS_TASK);
      let gender = answers[0].split(" ").first();

      const selectedHairColor = processing.getExtra(Processing.EXTRA_SELECTED_HAIR_COLOR, "other");
      const hairColor = selectedHairColor && selectedHairColor !== "other" ? selectedHairColor : answers[1];

      const selectedHairStyle = processing.getExtra(Processing.EXTRA_SELECTED_HAIR_STYLE, "default");
      const hairStyle = selectedHairStyle && selectedHairStyle !== "default" ? selectedHairStyle : "";

      const configId = selectedHairStyle && selectedHairStyle !== "default" && creative.hasExtra("hair_style_config_id")
        ? creative.getExtra("hair_style_config_id")
        : creative.getExtra("config_id");

      const skinColor = answers[2];
      const age = answers[3];

      const cropTaskResult = await photolabTaskHelper(creative, "crop", new PhotolabTaskBuilder()
        .addCollageMethod({template_name: 6455})
        .addImage(processing.file.url)
        .setLanguage(processing.language)
        .build());

      let finalTaskResult = await photolabTaskHelper(creative, "prompt", new PhotolabTaskBuilder()
        .setLanguage(processing.language)
        .addCollageMethod({
          "@transformationId": "npp_base",
          "@configId": configId,
          "@replacements": [
            {
              parameters: ["prompt"],
              search: "{{gender}}",
              mode: "ig",
              replacement: gender,
            },
            {
              parameters: ["prompt"],
              search: "{{age_group}}",
              mode: "ig",
              replacement: mapAgeGroupToPromptString(normalizeAgeGroup(age), gender),
            },
            {
              parameters: ["prompt"],
              search: "{{hair_color}}",
              mode: "ig",
              replacement: normalizeHairColor(hairColor),
            },
            {
              parameters: ["prompt"],
              search: "{{skin_color}}",
              mode: "ig",
              replacement: skinColor,
            },
            {
              parameters: ["prompt"],
              search: "{{hair_length}}",
              mode: "ig",
              replacement: hairStyle,
            },
          ],
          param_sds: creative.getExtra("seed"),
          gender: gender === "male" ? "man" : "woman",
        })
        .addImage(cropTaskResult.resultUrl)
        .setLanguage(processing.language)
        .build());

      const check = await checkImageIsAdultness(processing, finalTaskResult.resultUrl);
      if (check.isAdult) {
        creative.setExtra("adult", check.isAdult);
        throw BadResultError.nsfw();
      }

      if (creative.getExtra("isShouldRoop")) {
        finalTaskResult = await roopTask(processing, creative, cropTaskResult.resultUrl, finalTaskResult.resultUrl);
      }

      await promisifyImage(finalTaskResult.resultUrl);

      creative.setFile("raw", finalTaskResult.resultUrl);
      creative.markAsProcessed(finalTaskResult.resultUrl);

      defaultHandlerResolver(creative, resolve)();
    } catch (err) {
      defaultHandlerCatch(creative, reject)(err);
    }
  });
}

function normalizeHairColor(sourceColor) {
  sourceColor = (sourceColor || "").toLowerCase();
  sourceColor = sourceColor.split(" and ").first();
  sourceColor = sourceColor === "yellow" ? "blonde" : sourceColor;
  sourceColor = sourceColor === "white" ? "gray" : sourceColor;
  sourceColor = sourceColor === "brown" ? "brunette" : sourceColor;

  return sourceColor;
}

function normalizeAgeGroup(value) {
  const intValue = parseInt(value);
  const group = ageGroups.find((group) => {
    return intValue >= group.range[0] && intValue <= group.range[1];
  });

  if (group) {
    return group.id;
  }

  const strValue = ("" + value).toLowerCase();
  if (strValue === "old") {
    return "old";
  } else if (strValue === "middle aged") {
    return "adult";
  }

  return strValue;
}